import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { heroCopy } from "../lib/landingData";
import { MyceliumNetworkScene } from "../components/MyceliumNetworkScene";

function scrollToNetwork() {
  const element = document.getElementById("network");
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export function HeroSection() {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20">
      {/* 3D network background */}
      <div className="absolute inset-0 z-0">
        <MyceliumNetworkScene className="h-full w-full" />
      </div>

      {/* Gradient overlays for readability — hsl() required; --background is bare channels */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background via-background/80 to-background" />
      <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_30%,transparent_0%,hsl(var(--background))_75%)]" />

      {/* Floating ambient particles (CSS) */}
      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden opacity-30">
        <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-primary/20 blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-primary/20 blur-[120px] animate-pulse" style={{ animationDelay: "2s" }} />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter text-foreground mb-6"
        >
          <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/70 to-primary/40 [text-shadow:none]">
            {heroCopy.headlineAccent}
          </span>
          <span className="block text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3 text-foreground">
            {heroCopy.headline}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-10"
        >
          {heroCopy.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6"
        >
          <Button asChild size="lg" className="group min-w-[200px] shadow-[0_0_40px_-12px_var(--primary)]">
            <Link to={heroCopy.primaryCtaHref}>
              {heroCopy.primaryCta}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={scrollToNetwork}
            className="group min-w-[200px] border-primary/30 text-primary bg-primary/5 hover:bg-primary/10 hover:text-primary"
          >
            <Play className="mr-2 h-4 w-4" />
            {heroCopy.secondaryCta}
          </Button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="text-sm text-muted-foreground"
        >
          {heroCopy.microCopy}
        </motion.p>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="h-12 w-6 rounded-full border border-white/20 p-1">
          <motion.div
            animate={{ y: [0, 14, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="h-2 w-2 rounded-full bg-primary mx-auto"
          />
        </div>
      </motion.div>
    </section>
  );
}
